import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as Dialog from '@radix-ui/react-dialog'
import {
  Search,
  MapPin,
  Users,
  SlidersHorizontal,
  X,
  Grid,
  Map as MapIcon,
  Building2,
  ArrowLeft,
  Accessibility,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../lib/auth'
import { getAccessibilityFeature } from '@roomsi/shared'
import { MarketplaceLayout } from '../components/MarketplaceLayout'
import { MarketplaceRoomCard } from '../components/MarketplaceRoomCard'

// Props for city mode
export interface MarketplacePageProps {
  /** Pre-filter by city name (for city landing pages) */
  presetCity?: string
  /** Display name for the city (properly capitalized) */
  cityDisplayName?: string
}

// API types
interface MarketplaceRoom {
  id: string
  name: string
  slug: string
  description: string | null
  capacity: number
  hourlyRate: number | null
  amenities: string[]
  images: string[]
  heroImageIndex: number
  orgId: string
  orgName: string
  orgSlug: string
  buildingId: string | null
  buildingName: string | null
  buildingAddress: string | null
  city: string | null
  postcode: string | null
  country: string | null
  latitude: number | null
  longitude: number | null
  accessibilityFeatures: string[]
  accessibilityNotes: string | null
  averageRating: number | null
  reviewCount: number
}

interface MarketplaceFilters {
  cities: string[]
  amenities: string[]
  accessibilityFeatures: string[]
  priceRange: { min: number; max: number }
  capacityRange: { min: number; max: number }
}

interface MarketplaceResponse {
  rooms: MarketplaceRoom[]
  total: number
  filters: MarketplaceFilters
}

const API_BASE = '/api/v1'

// Map placeholder (we'll add actual map integration later)
function MapView({ rooms }: { rooms: MarketplaceRoom[] }) {
  const roomsWithLocation = rooms.filter((r) => r.latitude && r.longitude)

  if (roomsWithLocation.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-muted rounded-xl">
        <div className="text-center p-8">
          <MapPin className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No rooms with location data</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-muted rounded-xl relative overflow-hidden">
      {/* Placeholder for map - will integrate Leaflet or similar */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center p-8">
          <MapIcon className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-lg font-medium text-muted-foreground">Map View</p>
          <p className="text-sm text-muted-foreground mt-1">
            {roomsWithLocation.length} rooms with location data
          </p>
          <p className="text-xs text-muted-foreground mt-4">Interactive map coming soon</p>
        </div>
      </div>
      {/* Room markers preview */}
      <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium mb-2">Rooms in view:</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {roomsWithLocation.slice(0, 5).map((room) => (
            <Link
              key={room.id}
              to="/marketplace/$orgSlug/$roomSlug"
              params={{ orgSlug: room.orgSlug, roomSlug: room.slug }}
              className="flex-shrink-0 flex items-center gap-2 bg-muted px-3 py-1.5 rounded-full text-sm hover:bg-gray-100 transition-colors"
            >
              <MapPin className="h-3 w-3" />
              {room.name}
            </Link>
          ))}
          {roomsWithLocation.length > 5 && (
            <span className="flex-shrink-0 px-3 py-1.5 text-sm text-muted-foreground">
              +{roomsWithLocation.length - 5} more
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// Filter sidebar
function FilterSidebar({
  filters,
  activeFilters,
  onFilterChange,
  onClose,
  isMobile,
  hideCityFilter,
}: {
  filters: MarketplaceFilters | undefined
  activeFilters: {
    city?: string
    amenities: string[]
    accessibility: string[]
    minCapacity?: number
    maxPrice?: number
  }
  onFilterChange: (key: string, value: unknown) => void
  onClose?: () => void
  isMobile?: boolean
  hideCityFilter?: boolean
}) {
  if (!filters) return null

  return (
    <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
      {isMobile && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Filters</h2>
          <button onClick={onClose} aria-label="Close filters" title="Close filters" className="p-2 hover:bg-muted rounded-md">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* City filter - hidden when city is preset */}
      {!hideCityFilter && filters.cities.length > 0 && (
        <div>
          <label htmlFor={isMobile ? 'mobile-filter-city' : 'filter-city'} className="text-sm font-semibold mb-2 block">City</label>
          <select
            id={isMobile ? 'mobile-filter-city' : 'filter-city'}
            value={activeFilters.city || ''}
            onChange={(e) => onFilterChange('city', e.target.value || undefined)}
            className="w-full min-h-11 px-3 py-2 bg-white border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="">All cities</option>
            {filters.cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Capacity filter */}
      <div>
        <label htmlFor={isMobile ? 'mobile-filter-capacity' : 'filter-capacity'} className="text-sm font-semibold mb-2 block">Number of guests</label>
        <select
          id={isMobile ? 'mobile-filter-capacity' : 'filter-capacity'}
          value={activeFilters.minCapacity || ''}
          onChange={(e) =>
            onFilterChange('minCapacity', e.target.value ? parseInt(e.target.value) : undefined)
          }
          className="w-full min-h-11 px-3 py-2 bg-white border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="">Any capacity</option>
          {[2, 4, 6, 8, 10, 15, 20, 30, 50]
            .filter((n) => n <= (filters.capacityRange.max || 100))
            .map((n) => (
              <option key={n} value={n}>
                {n}+ guests
              </option>
            ))}
        </select>
      </div>

      {/* Price filter */}
      {filters.priceRange.max > 0 && (
        <div>
          <label htmlFor={isMobile ? 'mobile-filter-price' : 'filter-price'} className="text-sm font-semibold mb-2 block">Maximum price per hour</label>
          <select
            id={isMobile ? 'mobile-filter-price' : 'filter-price'}
            value={activeFilters.maxPrice || ''}
            onChange={(e) =>
              onFilterChange('maxPrice', e.target.value ? parseFloat(e.target.value) : undefined)
            }
            className="w-full min-h-11 px-3 py-2 bg-white border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="">Any price</option>
            {[10, 25, 50, 75, 100, 150, 200, 300, 500]
              .filter((n) => n <= filters.priceRange.max * 1.5)
              .map((n) => (
                <option key={n} value={n}>
                  Up to £{n}/hr
                </option>
              ))}
          </select>
        </div>
      )}

      {/* Amenities filter */}
      {filters.amenities.length > 0 && (
        <div>
          <label className="text-sm font-medium mb-2 block">Amenities</label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {filters.amenities.map((amenity) => (
              <label key={amenity} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeFilters.amenities.includes(amenity)}
                  onChange={(e) => {
                    const newAmenities = e.target.checked
                      ? [...activeFilters.amenities, amenity]
                      : activeFilters.amenities.filter((a) => a !== amenity)
                    onFilterChange('amenities', newAmenities)
                  }}
                  className="rounded border-border text-primary-600 focus:ring-primary-500"
                />
                {amenity}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Accessibility filter */}
      {filters.accessibilityFeatures && filters.accessibilityFeatures.length > 0 && (
        <div>
          <label className="text-sm font-medium mb-2 block flex items-center gap-1.5">
            <Accessibility className="h-4 w-4 text-community-strong" />
            Accessibility
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {filters.accessibilityFeatures.map((featureId) => {
              const feature = getAccessibilityFeature(featureId)
              if (!feature) return null
              return (
                <label key={featureId} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activeFilters.accessibility.includes(featureId)}
                    onChange={(e) => {
                      const newAccessibility = e.target.checked
                        ? [...activeFilters.accessibility, featureId]
                        : activeFilters.accessibility.filter((a) => a !== featureId)
                      onFilterChange('accessibility', newAccessibility)
                    }}
                    className="rounded border-border text-primary-600 focus:ring-primary-500"
                  />
                  {feature.label}
                </label>
              )
            })}
          </div>
        </div>
      )}

      {/* Clear filters */}
      {(activeFilters.city ||
        activeFilters.amenities.length > 0 ||
        activeFilters.accessibility.length > 0 ||
        activeFilters.minCapacity ||
        activeFilters.maxPrice) && (
        <button
          onClick={() => {
            onFilterChange('city', undefined)
            onFilterChange('amenities', [])
            onFilterChange('accessibility', [])
            onFilterChange('minCapacity', undefined)
            onFilterChange('maxPrice', undefined)
          }}
          className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Clear all filters
        </button>
      )}
    </div>
  )
}

// Main marketplace page
export function MarketplacePage({ presetCity, cityDisplayName }: MarketplacePageProps = {}) {
  const { checkAuth } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid')
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const filterTriggerRef = useRef<HTMLButtonElement>(null)
  const [activeFilters, setActiveFilters] = useState<{
    city?: string
    amenities: string[]
    accessibility: string[]
    minCapacity?: number
    maxPrice?: number
  }>({ amenities: [], accessibility: [], city: presetCity })

  // Check auth on mount
  useEffect(() => {
    checkAuth()
  }, [])

  // Determine if in city mode
  const isCityMode = Boolean(presetCity && cityDisplayName)

  // SEO content based on mode
  const seoTitle = isCityMode
    ? `Meeting Rooms & Event Spaces in ${cityDisplayName} | Roomsi`
    : 'Find Community Spaces Near You | Roomsi'

  const seoDescription = isCityMode
    ? `Find and book meeting rooms, event spaces, and community venues in ${cityDisplayName}. Village halls, church rooms, and local spaces for your club, class, or gathering.`
    : 'Discover village halls, church rooms, charity spaces and community venues for your club, class, meeting or event. Book local spaces that support your community.'

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (debouncedSearch) params.set('search', debouncedSearch)
    if (activeFilters.city) params.set('city', activeFilters.city)
    if (activeFilters.minCapacity) params.set('minCapacity', String(activeFilters.minCapacity))
    if (activeFilters.maxPrice) params.set('maxPrice', String(activeFilters.maxPrice))
    if (activeFilters.amenities.length > 0)
      params.set('amenities', activeFilters.amenities.join(','))
    if (activeFilters.accessibility.length > 0)
      params.set('accessibility', activeFilters.accessibility.join(','))
    return params.toString()
  }, [debouncedSearch, activeFilters])

  // Fetch marketplace rooms
  const { data, isLoading, error } = useQuery<MarketplaceResponse>({
    queryKey: ['marketplace', 'rooms', queryParams],
    queryFn: async () => {
      const url = queryParams
        ? `${API_BASE}/marketplace/rooms?${queryParams}`
        : `${API_BASE}/marketplace/rooms`
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch rooms')
      return response.json()
    },
  })

  const handleFilterChange = (key: string, value: unknown) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }))
  }

  // For city mode, don't count the preset city as a user-applied filter
  const activeFilterCount = [
    !presetCity && activeFilters.city, // Only count if not preset
    activeFilters.amenities.length > 0,
    activeFilters.minCapacity,
    activeFilters.maxPrice,
  ].filter(Boolean).length

  // Generate Schema.org data for city pages
  const citySchema =
    isCityMode && data
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `Meeting Rooms in ${cityDisplayName}`,
          description: seoDescription,
          numberOfItems: data.total,
          itemListElement: data.rooms.slice(0, 10).map((room, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'MeetingRoom',
              name: room.name,
              description: room.description || `Meeting room at ${room.orgName}`,
              url: `${window.location.origin}/marketplace/${room.orgSlug}/${room.slug}`,
              address: {
                '@type': 'PostalAddress',
                addressLocality: room.city,
                postalCode: room.postcode,
              },
              ...(room.averageRating && {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: room.averageRating,
                  reviewCount: room.reviewCount,
                },
              }),
            },
          })),
        }
      : null

  return (
    <MarketplaceLayout returnTo={isCityMode ? `/marketplace/city/${presetCity?.toLowerCase().replace(/\s+/g, '-')}` : '/marketplace'}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {isCityMode && (
          <link
            rel="canonical"
            href={`${window.location.origin}/marketplace/city/${presetCity?.toLowerCase().replace(/\s+/g, '-')}`}
          />
        )}
        {citySchema && <script type="application/ld+json">{JSON.stringify(citySchema)}</script>}
      </Helmet>

      {/* Hero Section - City-specific or generic */}
      <section className="border-b border-border bg-white py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {isCityMode ? (
            <>
              {/* Back to marketplace link for city pages */}
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 mb-2 sm:mb-4 text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to all spaces
              </Link>
              <h1 className="text-3xl sm:text-[40px] font-extrabold leading-tight mb-3 break-words">
                Rooms in {cityDisplayName}
              </h1>
              <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-4 sm:mb-8">
                Village halls, meeting rooms and local venues in {cityDisplayName}.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-[40px] font-extrabold leading-tight mb-3">
                Rooms for your community
              </h1>
              <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-4 sm:mb-8">
                Find a local room for your next class, club or get-together.
              </p>
            </>
          )}

          {/* Search bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <input
                type="search"
                aria-label="Search rooms"
                placeholder={
                  isCityMode
                    ? `Search rooms in ${cityDisplayName}`
                    : 'Town, room name or amenity'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full min-h-14 pl-11 sm:pl-12 pr-4 py-3 bg-card border border-input rounded-full text-base shadow-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 mb-12">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28 border-r border-border">
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5" />
                  <h2 className="font-semibold">Filters</h2>
                </div>
              </div>
              <FilterSidebar
                filters={data?.filters}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
                hideCityFilter={isCityMode}
              />
            </div>
          </aside>

          {/* Main content area */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex flex-wrap items-center gap-3">
                {/* Mobile filter button */}
                <button
                  ref={filterTriggerRef}
                  onClick={() => setShowMobileFilters(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Results count */}
                <p aria-live="polite" className="text-sm text-muted-foreground">
                  {isLoading
                    ? 'Loading...'
                    : data
                      ? `${data.total} ${data.total === 1 ? 'room' : 'rooms'} available`
                      : null}
                </p>
              </div>

              {/* View toggle */}
              <div role="group" aria-label="Room view" className="flex shrink-0 items-center bg-muted rounded-md p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' ? 'bg-background shadow-sm' : 'hover:bg-background/50'
                  }`}
                  title="Grid view"
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  aria-label="Map view"
                  aria-pressed={viewMode === 'map'}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'map' ? 'bg-background shadow-sm' : 'hover:bg-background/50'
                  }`}
                  title="Map view"
                >
                  <MapIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Active filters display */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {activeFilters.city && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-900 text-white rounded-full text-sm">
                    <MapPin className="h-3 w-3" />
                    {activeFilters.city}
                    <button
                      onClick={() => handleFilterChange('city', undefined)}
                      aria-label="Remove city filter"
                      className="hover:bg-white/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {activeFilters.minCapacity && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-900 text-white rounded-full text-sm">
                    <Users className="h-3 w-3" />
                    {activeFilters.minCapacity}+ guests
                    <button
                      onClick={() => handleFilterChange('minCapacity', undefined)}
                      aria-label="Remove capacity filter"
                      className="hover:bg-white/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {activeFilters.maxPrice && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-900 text-white rounded-full text-sm">
                    Up to £{activeFilters.maxPrice}/hr
                    <button
                      onClick={() => handleFilterChange('maxPrice', undefined)}
                      aria-label="Remove price filter"
                      className="hover:bg-white/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {activeFilters.amenities.map((amenity) => (
                  <span
                    key={amenity}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-900 text-white rounded-full text-sm"
                  >
                    {amenity}
                    <button
                      onClick={() =>
                        handleFilterChange(
                          'amenities',
                          activeFilters.amenities.filter((a) => a !== amenity)
                        )
                      }
                      aria-label={`Remove ${amenity} filter`}
                      className="hover:bg-white/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Content */}
            {error ? (
              <div className="text-center py-16">
                <p className="text-red-600">Failed to load spaces. Please try again later.</p>
              </div>
            ) : isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-card rounded-xl border border-border overflow-hidden animate-pulse"
                  >
                    <div className="aspect-[20/19] bg-muted" />
                    <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                      <div className="h-5 sm:h-6 bg-muted rounded w-3/4" />
                      <div className="h-3 sm:h-4 bg-muted rounded w-1/2" />
                      <div className="h-3 sm:h-4 bg-muted rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : viewMode === 'grid' ? (
              data?.rooms && data.rooms.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {data.rooms.map((room) => (
                    <MarketplaceRoomCard key={`${room.orgId}-${room.id}`} room={{ ...room, image: room.images[room.heroImageIndex] || room.images[0], location: room.city, accessibilityFeatureCount: room.accessibilityFeatures?.length }} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Building2 className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h2 className="text-xl font-semibold mb-2">No rooms found</h2>
                  <p className="text-muted-foreground">
                    {activeFilterCount > 0 || debouncedSearch
                      ? 'Try adjusting your filters or search terms'
                      : 'No spaces are currently available in the marketplace'}
                  </p>
                </div>
              )
            ) : (
              <div className="h-[600px]">
                <MapView rooms={data?.rooms || []} />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile filters drawer */}
      <Dialog.Root open={showMobileFilters} onOpenChange={setShowMobileFilters}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/50" />
          <Dialog.Content aria-describedby={undefined} onCloseAutoFocus={(event) => { event.preventDefault(); filterTriggerRef.current?.focus() }} className="fixed right-0 top-0 bottom-0 z-50 w-80 max-w-full bg-card shadow-xl overflow-y-auto">
            <Dialog.Title className="sr-only">Filter rooms</Dialog.Title>
            <FilterSidebar
              filters={data?.filters}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onClose={() => setShowMobileFilters(false)}
              isMobile
              hideCityFilter={isCityMode}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Browse by City Section - for SEO (hide when already on city page) */}
      {!isCityMode && data?.filters?.cities && data.filters.cities.length > 0 && (
        <section className="bg-muted/50 py-12 mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold mb-2">Rooms by location</h2>
            <p className="text-muted-foreground mb-6">
              Find meeting rooms and event spaces in your area
            </p>
            <div className="flex flex-wrap gap-2">
              {data.filters.cities.map((city) => (
                <Link
                  key={city}
                  to="/marketplace/city/$citySlug"
                  params={{ citySlug: city.toLowerCase().replace(/\s+/g, '-') }}
                  className="px-4 py-2 bg-card border border-border rounded-lg text-sm hover:border-primary-500 hover:text-primary-600 transition-colors"
                >
                  {city}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

    </MarketplaceLayout>
  )
}
